import { addDays, clockLabel, isoDay, shortDayLabel } from 'helper/timeLabels';

// Pure helpers of the call list. They return text keys (relative to the
// `prospection` locale block) and values, never text.

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];
const DAYS = [
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
];

// A relaunch is the callback a lost company's relaunch date sets
// (JBWEB-1423): only its title differs.
const isPlanned = (line) =>
  line.kind === 'callback' || line.kind === 'retry' || line.kind === 'relaunch';

export const splitLines = (pending) => {
  const lines = pending || [];
  return {
    planned: lines.filter(isPlanned),
    queue: lines.filter((line) => line.kind === 'new'),
  };
};

export const dayState = (day) => {
  if ((day.pending || []).length > 0) return 'active';
  return (day.made || []).length > 0 ? 'finished' : 'empty';
};

export const goalProgress = (counts) => {
  const part = ({ done, goal }) => ({
    done,
    goal,
    shown: Math.min(done, goal),
  });
  const discovery = part(counts.discovery);
  const followup = part(counts.followup);
  const total = discovery.goal + followup.goal;
  const reached = discovery.shown + followup.shown;
  return {
    discovery,
    followup,
    total,
    reached,
    remaining: Math.max(0, total - reached),
  };
};

export const lastCallAt = (made) =>
  (made || []).reduce(
    (latest, call) => (!latest || call.at > latest ? call.at : latest),
    null,
  );

export const durationText = (fromIso, toIso) => {
  // Rounded first, so that 59.5 minutes reads « 1 h », not « 60 minutes ».
  const minutes = Math.round((new Date(toIso) - new Date(fromIso)) / 60000);
  if (minutes < 60) {
    return {
      key: 'duration.minutes',
      count: Math.max(1, minutes),
      values: {},
    };
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return { key: 'duration.hours', values: { hours } };
  return {
    key: 'duration.hoursMinutes',
    values: { hours, minutes: String(rest).padStart(2, '0') },
  };
};

export const statusLine = ({ editing, day }) => {
  if (editing) return { key: 'status.editing', values: {}, tone: 'default' };
  const { remaining, total } = goalProgress(day.counts);
  // Zero goals is never a reached goal.
  const reached = total > 0 && remaining === 0;
  if (dayState(day) === 'finished') {
    return {
      key: reached ? 'status.finishedGoal' : 'status.finished',
      values: {},
      duration: durationText(day.daySession.startedAt, lastCallAt(day.made)),
      tone: reached ? 'success' : 'default',
    };
  }
  if (reached)
    return { key: 'status.goalReached', values: {}, tone: 'success' };
  return {
    key: 'status.remaining',
    count: remaining,
    values: {},
    tone: 'default',
  };
};

export const attemptNumber = (line) => {
  if (line.kind === 'retry') return line.attempt + 1;
  if (line.kind === 'new' && line.history && line.history.count > 0) {
    return line.history.missed + 1;
  }
  return 1;
};

export const attemptTag = (line, now) => {
  const n = attemptNumber(line);
  const base = line.type === 'followup' ? 'followup' : 'discovery';
  const label =
    n === 1
      ? { key: `tags.${base}`, values: {} }
      : { key: `tags.${base}Attempt`, values: { n } };

  let suffix = null;
  if (isPlanned(line)) {
    const due = new Date(line.dueAt);
    const time = clockLabel(due);
    suffix =
      isoDay(due) === isoDay(now)
        ? { key: 'tags.atTime', values: { time } }
        : {
            key: 'tags.atDay',
            values: { day: shortDayLabel(isoDay(due)), time },
          };
  }
  return { label, suffix, tone: line.type };
};

const OVERSEAS_PREFIXES = [
  ['+596', ['0596', '0696', '0697']],
  ['+590', ['0590', '0690', '0691']],
  ['+594', ['0594', '0694']],
  ['+262', ['0262', '0263', '0692', '0693', '0269', '0639']],
];

export const telHref = (phone) => {
  const raw = String(phone || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  // « +33 06… »: the trunk 0 has no place after the country code.
  if (raw.startsWith('+')) return `tel:+${digits.replace(/^(33|590|594|596|262)0/, '$1')}`;
  if (digits.startsWith('00')) return `tel:+${digits.slice(2)}`;
  if (digits.length === 10 && digits.startsWith('0')) {
    const prefix = digits.slice(0, 4);
    const found = OVERSEAS_PREFIXES.find(([, prefixes]) =>
      prefixes.includes(prefix),
    );
    return `tel:${found ? found[0] : '+33'}${digits.slice(1)}`;
  }
  return `tel:${digits}`;
};

export const firstNameOf = (contactName, companyName) => {
  const words = String(contactName || '')
    .trim()
    .split(/\s+/)
    .filter((word) => word && !/^dr\.?$/i.test(word));
  return words[0] || companyName;
};

const SOURCES = [
  'phone',
  'field',
  'referral',
  'search',
  'social',
  'inbound',
  'field_visit',
];

export const sourceKey = (code) =>
  SOURCES.includes(code) ? `sources.${code}` : null;

const dayOf = (iso) => shortDayLabel(isoDay(new Date(iso)));

export const globalMotif = (prospect) => {
  const { company, history, createdAt, type } = prospect;
  if (!history) {
    return {
      key:
        company && company.source === 'inbound'
          ? 'global.motif.inbound'
          : 'global.motif.added',
      values: { date: dayOf(createdAt) },
    };
  }
  const date = dayOf(history.lastCallAt);
  if (type === 'followup')
    return { key: 'global.motif.lastCall', values: { date } };
  const count = history.missed;
  return {
    key: count === 1 ? 'global.motif.missedOne' : 'global.motif.missedOther',
    count,
    values: { date },
  };
};

export const globalTabs = (counts) => {
  const left = ({ goal, done, planned }) => Math.max(0, goal - done - planned);
  const discovery = left(counts.discovery);
  const followup = left(counts.followup);
  return {
    discovery,
    followup,
    initialTab: discovery === 0 && followup > 0 ? 'followup' : 'discovery',
  };
};

export const inDayValues = (counts) => ({
  discovery: counts.discovery.done + counts.discovery.planned,
  discoveryGoal: counts.discovery.goal,
  followup: counts.followup.done + counts.followup.planned,
  followupGoal: counts.followup.goal,
});

const normalize = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export const filterProspects = (prospects, { type, query }) => {
  const needle = normalize(query).trim();
  return (prospects || []).filter((prospect) => {
    if (type && prospect.type !== type) return false;
    if (!needle) return true;
    const { company } = prospect;
    return [company.name, company.city, company.contactName].some((field) =>
      normalize(field).includes(needle),
    );
  });
};

const localDayNumber = (date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

export const pickSuggestions = (prospects, now, limit = 3) =>
  [...(prospects || [])]
    .sort((a, b) => {
      if (!a.history || !b.history)
        return (a.history ? 1 : 0) - (b.history ? 1 : 0);
      return new Date(a.history.lastCallAt) - new Date(b.history.lastCallAt);
    })
    .slice(0, limit)
    .map((prospect) => {
      if (!prospect.history)
        return { prospect, subtitle: { key: 'side.never', values: {} } };
      const days = Math.round(
        (localDayNumber(now) -
          localDayNumber(new Date(prospect.history.lastCallAt))) /
          86400000,
      );
      return {
        prospect,
        subtitle:
          days <= 0
            ? { key: 'side.today', values: {} }
            : { key: 'side.since', count: days, values: {} },
      };
    });

export const endSummary = (made) => ({
  reached: (made || []).filter(
    (call) => call.result === 'answered' || call.result === 'callback',
  ).length,
  callbacks: (made || []).filter(
    (call) => call.result === 'callback' || call.result === 'no_answer',
  ).length,
});

export const upcomingText = (upcoming) => {
  // An api without the `upcoming` field (before JBWEB-1394 api) reads as none.
  const { callbacks = 0, retries = 0 } = upcoming || {};
  if (callbacks === 0) return { key: 'side.upcomingNone', values: {} };
  if (retries === 0) {
    return callbacks === 1
      ? { key: 'side.upcomingOne', values: {} }
      : { key: 'side.upcomingOther', count: callbacks, values: {} };
  }
  if (callbacks === 1) {
    return { key: 'side.upcomingOneMissed', count: retries, values: {} };
  }
  return {
    key: retries === 1 ? 'side.upcomingMissedOne' : 'side.upcomingMissedOther',
    count: retries,
    values: { total: callbacks },
  };
};

export const postponedToast = (dueAt, now) => {
  const due = new Date(dueAt);
  const time = clockLabel(due);
  if (isoDay(due) === isoDay(addDays(now, 1))) {
    return { key: 'toasts.postponedTomorrow', values: { time } };
  }
  return {
    key: 'toasts.postponedDay',
    values: {
      time,
      day: `${DAYS[due.getDay()]} ${due.getDate()} ${MONTHS[due.getMonth()]}`,
    },
  };
};

export const lineOfCompany = (pending, companyId) =>
  (pending || []).find(
    (line) => line.company && line.company.id === companyId,
  ) || null;

/**
 * The lines to call in their order: the planned callbacks first, at their
 * time, then the queue (`kind: 'new'`) in the order of `ids` — the one just
 * dragged, until the api answers with the day it keeps; a line missing from
 * `ids` comes at the end, in the api's order.
 */
export const orderLines = (pending, ids = []) => {
  const fixed = pending.filter((line) => line.kind !== 'new');
  const queue = pending.filter((line) => line.kind === 'new');
  const rank = (line) => {
    const at = ids.indexOf(line.id);
    return at === -1 ? ids.length + queue.indexOf(line) : at;
  };
  return [...fixed, ...[...queue].sort((a, b) => rank(a) - rank(b))];
};

// The callbacks planned on a later day than `now`'s (« Relances planifiées »),
// from the prospects of /prospects, the soonest first: { id, company, dueAt }.
export const upcomingCallbacks = (prospects, now) => {
  const today = isoDay(now);
  return (prospects || [])
    .filter(
      (row) =>
        row.nextCallback?.dueAt &&
        isoDay(new Date(row.nextCallback.dueAt)) > today,
    )
    .map((row) => ({
      id: row.nextCallback.id,
      company: row.company,
      dueAt: row.nextCallback.dueAt,
    }))
    .sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1));
};
